import { isDeployEnforcementHandling, observeDeployVersion } from "@/domains/shared/services/deployVersion";
import type { AxiosInstance, AxiosResponse, AxiosResponseHeaders, RawAxiosResponseHeaders } from "axios";

type DeployVersionHeaders = {
  version: string;
  offlineSchema: number;
};

type DeployHandledError = {
  __mivoDeployHandled?: boolean;
};

function readHeader(
  headers: AxiosResponseHeaders | Partial<RawAxiosResponseHeaders> | undefined,
  name: string,
): string | null {
  if (!headers) {
    return null;
  }

  if ("get" in headers && typeof headers.get === "function") {
    const value = headers.get(name);
    return typeof value === "string" ? value : null;
  }

  const match = Object.entries(headers).find(([key]) => key.toLowerCase() === name.toLowerCase());
  const value = match?.[1];
  return typeof value === "string" ? value : null;
}

function readDeployVersionHeaders(
  response: AxiosResponse | undefined,
): DeployVersionHeaders | null {
  const version = readHeader(response?.headers, "x-mivo-deployment-version")?.trim();
  const schemaRaw = readHeader(response?.headers, "x-mivo-offline-schema")?.trim();
  const offlineSchema = schemaRaw == null ? Number.NaN : Number.parseInt(schemaRaw, 10);

  if (!version || Number.isNaN(offlineSchema)) {
    return null;
  }

  return { version, offlineSchema };
}

export function setupDeployVersionInterceptor(api: AxiosInstance): void {
  api.interceptors.response.use(
    async (response) => {
      const deployHeaders = readDeployVersionHeaders(response);

      if (deployHeaders && !isDeployEnforcementHandling()) {
        await observeDeployVersion(deployHeaders.version, deployHeaders.offlineSchema);
      }

      return response;
    },
    async (error: DeployHandledError & { response?: AxiosResponse }) => {
      const deployHeaders = readDeployVersionHeaders(error.response);

      if (deployHeaders && !isDeployEnforcementHandling()) {
        const enforced = await observeDeployVersion(deployHeaders.version, deployHeaders.offlineSchema);

        if (enforced) {
          error.__mivoDeployHandled = true;
        }
      }

      return Promise.reject(error);
    },
  );
}
